const REACT_APP_BFF_URL = process.env.REACT_APP_BFF_URL;
const ABSOLUTE_URL_REGEX = /^(?:https?:|data:|blob:)/i;

const buildPublicImageUrl = (path) => {
    if (!path || ABSOLUTE_URL_REGEX.test(path)) {
        return path;
    }
    const base = (REACT_APP_BFF_URL || "").replace(/\/$/, "");
    const downloadPath = `/api/file-upload/download?path=${encodeURIComponent(path)}`;
    return base ? `${base}${downloadPath}` : downloadPath;
};

// Helper function to get download URLs for images
const getImageDownloadUrls = async (token, images) => {
    if (!images || !Array.isArray(images)) return [];
    
    try {
        const { getMultipleImageDownloadUrls } = await import('./FileUploadServices');
        return await getMultipleImageDownloadUrls(token, images);
    } catch (error) {
        console.error('Error getting image download URLs:', error);
        return [];
    }
};

// Helper function to get download URL for technical sheet
const getTechnicalSheetDownloadUrl = async (token, technicalSheetPath) => {
    if (!technicalSheetPath) return null;
    
    try {
        const { getImageDownloadUrl } = await import('./FileUploadServices');
        return await getImageDownloadUrl(token, technicalSheetPath);
    } catch (error) {
        console.error('Error getting technical sheet download URL:', error);
        return null;
    }
};

export const getProducts = async (token = null) => {
    const response = await fetch(`${REACT_APP_BFF_URL}/products`, {
        method: 'GET',
        headers: {
            'Content-Type': 'application/json',
        }
    });
    if (!response.ok) {
        throw new Error('Error al obtener los productos.');
    }

    const products = await response.json();

    // Process all products
    for (const product of products) {
        // Process images
        if (product.images && Array.isArray(product.images)) {
            // If token is provided, try to get signed download URLs
            if (token) {
                const signedUrls = await getImageDownloadUrls(token, product.images);
                // Use signed URLs if available, otherwise keep original paths
                if (signedUrls && signedUrls.length > 0) {
                    product.images = signedUrls;
                }
            }

            // Always ensure all images have proper public URLs (this will preserve absolute URLs)
            product.images = product.images.map((image) => buildPublicImageUrl(image));
        }

        // Process technical sheet
        if (token && product.technical_sheet) {
            product.technical_sheet = await getTechnicalSheetDownloadUrl(token, product.technical_sheet);
        }
    }

    return products;
}

export const getProduct = async (productId, token = null) => {
    const response = await fetch(`${REACT_APP_BFF_URL}/products/${productId}`, {
        method: 'GET',
        headers: {
            'Content-Type': 'application/json',
        }
    });
    if (!response.ok) {
        throw new Error('Error al obtener el producto.');
    }

    const product = await response.json();

    // Process images - always convert to public URLs
    if (product.images && Array.isArray(product.images)) {
        // If token is provided, try to get signed download URLs
        if (token) {
            const signedUrls = await getImageDownloadUrls(token, product.images);
            // Use signed URLs if available, otherwise keep original paths
            if (signedUrls && signedUrls.length > 0) {
                product.images = signedUrls;
            }
        }

        // Always ensure all images have proper public URLs (this will preserve absolute URLs)
        product.images = product.images.map((image) => buildPublicImageUrl(image));
    }

    if (token && product.technical_sheet) {
        product.technical_sheet = await getTechnicalSheetDownloadUrl(token, product.technical_sheet);
    }

    return product;
};

export const getProductsBySupplierId = async (userId, token = null) => {
    const response = await fetch(`${REACT_APP_BFF_URL}/products/supplier/uid/${userId}`, {
        method: 'GET',
        headers: {
            'Content-Type': 'application/json',
        }
    });
    if (!response.ok) {
        throw new Error('Error al obtener el producto.');
    }

    const products = await response.json();

    // Process all products
    for (const product of products) {
        // Process images
        if (product.images && Array.isArray(product.images)) {
            // If token is provided, try to get signed download URLs
            if (token) {
                const signedUrls = await getImageDownloadUrls(token, product.images);
                // Use signed URLs if available, otherwise keep original paths
                if (signedUrls && signedUrls.length > 0) {
                    product.images = signedUrls;
                }
            }

            // Always ensure all images have proper public URLs (this will preserve absolute URLs)
            product.images = product.images.map((image) => buildPublicImageUrl(image));
        }

        // Process technical sheet
        if (token && product.technical_sheet) {
            product.technical_sheet = await getTechnicalSheetDownloadUrl(token, product.technical_sheet);
        }
    }

    return products;
};

export const createProduct = async (token, name, details, use, materials, certifications, images, measures, technical_sheet, category) => {
    const response = await fetch(`${REACT_APP_BFF_URL}/products`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
            name: name,
            details: details,
            use: use,
            materials: materials,
            certifications: certifications,
            images: images,
            measures: measures,
            technical_sheet: technical_sheet,
            category: category
        }),
    });

    if (!response.ok) {
        throw new Error('Error al crear el producto.');
    }

    return await response.json();
};


export const updateProduct = async (productId, token, name, details, use, materials, certifications, images, measures, technical_sheet, category) => {
    const response = await fetch(`${REACT_APP_BFF_URL}/products/${productId}`, {
        method: 'PUT',
        headers: {
            'Content-Type': 'application/json',
            'Authorization': 'Bearer ' + token
        },
        body: JSON.stringify({
            name: name,
            details: details,
            use: use,
            materials: materials,
            certifications: certifications,
            images: images,
            measures: measures,
            technical_sheet: technical_sheet,
            category: category
        }),
    });

    if (!response.ok) {
        throw new Error('Error al actualizar el producto.');
    }

    return await response.json();
};

export const deleteProduct = async (token, productId) => {
    const response = await fetch(`${REACT_APP_BFF_URL}/products/${productId}`, {
        method: 'DELETE',
        headers: {
            'Content-Type': 'application/json',
            'Authorization': 'Bearer ' + token
        }
    });

    if (!response.ok) {
        throw new Error('Error al actualizar el producto.');
    }

    return await response.json();
};

export const getMyProducts = async (token) => {
    const response = await fetch(`${REACT_APP_BFF_URL}/products/supplier/me`, {
        method: 'GET',
        headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
        }
    });
    if (!response.ok) {
        throw new Error('Error al obtener los productos.');
    }

    const products = await response.json();

    // Process all products
    for (const product of products) {
        // Process images
        if (product.images && Array.isArray(product.images)) {
            // If token is provided, try to get signed download URLs
            if (token) {
                const signedUrls = await getImageDownloadUrls(token, product.images);
                // Use signed URLs if available, otherwise keep original paths
                if (signedUrls && signedUrls.length > 0) {
                    product.images = signedUrls;
                }
            }

            // Always ensure all images have proper public URLs (this will preserve absolute URLs)
            product.images = product.images.map((image) => buildPublicImageUrl(image));
        }

        // Process technical sheet
        if (token && product.technical_sheet) {
            product.technical_sheet = await getTechnicalSheetDownloadUrl(token, product.technical_sheet);
        }
    }

    return products;
}

export const getRelatedProducts = async (productId, token = null) => {
    const response = await fetch(`${REACT_APP_BFF_URL}/products/${productId}/related`, {
        method: 'GET',
        headers: {
            'Content-Type': 'application/json',
        }
    });

    if (!response.ok) {
        throw new Error('Error al obtener productos relacionados.');
    }

    const products = await response.json();

    // Process all products
    for (const product of products) {
        // Process images
        if (product.images && Array.isArray(product.images)) {
            // If token is provided, try to get signed download URLs
            if (token) {
                const signedUrls = await getImageDownloadUrls(token, product.images);
                // Use signed URLs if available, otherwise keep original paths
                if (signedUrls && signedUrls.length > 0) {
                    product.images = signedUrls;
                }
            }

            // Always ensure all images have proper public URLs (this will preserve absolute URLs)
            product.images = product.images.map((image) => buildPublicImageUrl(image));
        }

        // Process technical sheet
        if (token && product.technical_sheet) {
            product.technical_sheet = await getTechnicalSheetDownloadUrl(token, product.technical_sheet);
        }
    }

    return products;
};

export const searchProducts = async ({
    query = "",
    categories = [],
    certifications = [],
    materials = [],
    sortOrder = "default",
    page = 1,
    pageSize = 24,
} = {}) => {
    const params = new URLSearchParams();
    if (query) {
        params.append("q", query);
    }
    if (categories.length > 0) {
        params.append("categories", categories.join(","));
    }
    if (certifications.length > 0) {
        params.append("certifications", certifications.join(","));
    }
    if (materials.length > 0) {
        params.append("materials", materials.join(","));
    }
    params.append("sort", sortOrder);
    params.append("page", page);
    params.append("page_size", pageSize);

    const response = await fetch(`${REACT_APP_BFF_URL}/products/search?${params.toString()}`, {
        method: 'GET',
        headers: {
            'Content-Type': 'application/json',
        }
    });

    if (!response.ok) {
        throw new Error('Error al buscar productos.');
    }

    const payload = await response.json();

    if (Array.isArray(payload?.items)) {
        payload.items = payload.items.map((product) => {
            const images = Array.isArray(product.images)
                ? product.images.map((image) => buildPublicImageUrl(image))
                : product.images;
            const imageUrl = product.imageUrl
                ? buildPublicImageUrl(product.imageUrl)
                : Array.isArray(images) && images.length > 0
                ? images[0]
                : product.imageUrl;

            return {
                ...product,
                images,
                imageUrl,
            };
        });
    }

    return payload;
};

export const searchProductsVector = async (query = "", limit = 10, token = null) => {
    const params = new URLSearchParams();
    if (query) {
        params.append('q', query);
    }
    if (limit) {
        params.append('limit', limit);
    }

    const response = await fetch(`${REACT_APP_BFF_URL}/products/search/vector?${params.toString()}`, {
        method: 'GET',
        headers: {
            'Content-Type': 'application/json',
        }
    });

    if (!response.ok) {
        throw new Error('Error al buscar productos con IA.');
    }

    const payload = await response.json();

    // Process all results
    if (Array.isArray(payload.results)) {
        for (const product of payload.results) {
            // Process images
            if (product.images && Array.isArray(product.images)) {
                // If token is provided, try to get signed download URLs
                if (token) {
                    const signedUrls = await getImageDownloadUrls(token, product.images);
                    // Use signed URLs if available, otherwise keep original paths
                    if (signedUrls && signedUrls.length > 0) {
                        product.images = signedUrls;
                    }
                }

                // Always ensure all images have proper public URLs (this will preserve absolute URLs)
                product.images = product.images.map((image) => buildPublicImageUrl(image));
            }

            // Process technical sheet
            if (token && product.technical_sheet) {
                product.technical_sheet = await getTechnicalSheetDownloadUrl(token, product.technical_sheet);
            }

            // Set imageUrl if not present
            if (!product.imageUrl && Array.isArray(product.images) && product.images.length > 0) {
                product.imageUrl = product.images[0];
            }
        }
    }

    return payload;
};

export const getCategories = async () => {
    const response = await fetch(`${REACT_APP_BFF_URL}/categories`);
    if (!response.ok) {
        throw new Error('Error al obtener las categorías.');
    }
    return await response.json();
};

export const bulkCreateProducts = async (token, file) => {
    const formData = new FormData();
    formData.append('file', file);

    const response = await fetch(`${REACT_APP_BFF_URL}/products/bulk`, {
        method: 'POST',
        headers: {
            'Authorization': `Bearer ${token}`
        },
        body: formData,
    });

    if (!response.ok) {
        let errorText;
        try {
            errorText = await response.text();
        } catch (e) {
            errorText = 'Error desconocido en la carga masiva.';
        }
        throw new Error(errorText || 'Error al procesar la carga masiva.');
    }

    // Could be JSON or plain text; try JSON first
    try {
        return await response.json();
    } catch (e) {
        return { status: 'ok' };
    }
};
